import { BehaviorSubject, catchError, forkJoin, from, of, firstValueFrom } from 'rxjs';
import {Injectable} from '@angular/core';
import {environment} from '@env/environment';
import {HttpClient} from '@angular/common/http';
import {appConfig} from 'app/constants/url-mapping.constant';
import {UtilsService} from '@core/util-services/utils.service';

/**
 * Service to manage application configuration.
 * Handles loading configuration from S3 or local files based on the institution's rollout status.
 */
@Injectable({
    providedIn: 'root'
})
export class AppConfigService {
    // Static configuration object accessible globally
    static config: any = {};

    // Observable to notify subscribers when the app configuration is updated
    public onAppConfigUpdated$: BehaviorSubject<any> = new BehaviorSubject(null);

    // Institution ID from the environment configuration
    private institutionId = environment.institutionId;

    // URLs for fetching configuration files from S3
    private s3URL = `${environment.s3ConfigUrl}cs-config/institution-config/V2/${this.institutionId.toUpperCase()}_config.json`;
    private s3CommonURL = `${environment.s3ConfigUrl}cs-config/institution-config/V2/common_config.json`;

    constructor(private http: HttpClient, private utilsService: UtilsService) {
    }

    /**
     * Retrieves the static configuration object.
     * @returns The current application configuration.
     */
    static getConfig() {
        return AppConfigService.config;
    }

    /**
     * Initializes the application configuration.
     * Loads configuration from S3 or local files based on the institution's rollout status.
     * @returns A promise that resolves when the configuration is successfully loaded.
     */
    init(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (!appConfig.nonRolloutInstitutions.includes(this.institutionId)) {
                // Load configuration from S3 for rollout institutions
                this.loadFromS3().then(resolve).catch(reject);
            } else {
                // Load local configuration for non-rollout institutions
                this.loadLocalConfig().then(resolve).catch(reject);
            }
        });
    }

    /**
     * Fetches data from a given URL.
     * @param url The URL to fetch data from.
     * @returns A promise that resolves with the fetched data.
     */
    async fetchData(url: string): Promise<any> {
        const response = await fetch(url);
        const data = await response.json();
        return data;
    }

    /**
     * Retrieves a value from the configuration by key.
     * @param key The key to retrieve the value for.
     * @returns The value associated with the key.
     */
    get(key: string): any {
        // Method implementation
    }

    /**
     * Sets a value in the configuration for a given key.
     * @param key The key to set the value for.
     * @param value The value to set.
     */
    set(key: string, value: any): void {
        // Method implementation
    }

    /**
     * Loads configuration from S3.
     * Merges common and institution-specific configurations and updates the static config object.
     * @returns A promise that resolves when the configuration is successfully loaded.
     */
    private async loadFromS3(): Promise<void> {
                try {
        const { common, institution } = await firstValueFrom(
          forkJoin({
            common: from(this.fetchData(this.s3CommonURL)).pipe(
              catchError(err => {
                console.error('❌ Failed to fetch common config:', err);
                return of(null);
              })
            ),
            institution: from(this.fetchData(this.s3URL)).pipe(
              catchError(err => {
                console.warn('⚠️ Failed to fetch institution config, using common only:', err);
                return of(null);
              })
            )
          })
        );

        if (!common) {
          throw new Error('Common config is required but failed to load from S3');
        }

        // Merge configs and assign allLoanPurposes internally
        AppConfigService.config = this.mergeConfigs(common, institution);

      } catch (error) {
        console.error('Error during S3 config loading:', error);
        throw error;
      }
    }

    private mergeConfigs(common: any, institution: any): any {
      // Start with a deep copy of common
                    const mergedCommon = this.utilsService.mergeDeep(common, institution?.common || {});

      // Merge lending/deposit overrides
      const finalConfig = this.utilsService.mergeDeep(mergedCommon, institution?.deposit || {});

      // Assign allLoanPurposes from institution common if available, fallback to merged common
      finalConfig.allLoanPurposes = {
        ...(institution?.common?.loanPurpose || {}),
        ...(institution?.lending?.loanPurpose || {}),
        ...(institution?.deposit?.loanPurpose || {})
      };

      return finalConfig;
    }

    /**
     * Loads configuration from local files.
     * Falls back to a default configuration if institution-specific config fails.
     * @returns A promise that resolves when the configuration is successfully loaded.
     */
    private loadLocalConfig(): Promise<void> {
        return forkJoin({
            institutionConfig: from(import(`../../../config/institutionConfig/config-${this.institutionId.toLowerCase()}`))
                .pipe(catchError(() => of(null))), // Fallback to null if institution-specific config fails
            defaultConfig: from(import(`../../../config/institutionConfig/config`))
                .pipe(catchError(() => of(null))) // Fallback to null if default config fails
        })
            .toPromise()
            .then(({institutionConfig, defaultConfig}) => {
                if (!institutionConfig && !defaultConfig) {
                    throw new Error('Failed to load both institution-specific and default configurations');
                }

                // Use institution-specific config if available, otherwise fallback to default config
                const config = institutionConfig?.config || defaultConfig?.config;

                // Update the static config object with the loaded configuration
                AppConfigService.config = config;
                this.onAppConfigUpdated$.next(AppConfigService.config);
                console.log('Loaded local config:', config);
            })
            .catch((err) => {
                console.error('Failed to load local config:', err);
                throw err;
            });
    }
}
